import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { formatRelative } from '../../core/format.js';
import { reindexBody } from '../../core/parity.js';
import { empty } from '../../components/empty.js';
import { skeleton } from '../../components/skeleton.js';
import { toast } from '../../components/toast.js';
import { loadKnowledge, fetchMemoryPage, fetchDocumentPage } from './load.js';
import { memoryGroup, memorySplit } from './model.js';
import { DOC_PAGE, MEMORY_PAGE, PAGE_CAP, countsOf, dropCounted, mergeRows, upsertCounted, upsertRow } from './paging.js';
import { renderInstructions } from './instructions.js';
import { renderDocuments } from './documents.js';
import { renderMemory } from './memory.js';
import { renderSide } from './side.js';
import { renderEvents } from './events.js';
import { kbFileAllowed, KB_MAX_BYTES, renderDropZone, uploadDocument } from './upload.js';
import { claimReindex } from './reindex.js';
import { openLinkedDocument } from './viewer.js';

function tabFromUrl() {
  const tab = new URLSearchParams(window.location.search).get('tab');
  if (tab === 'documents' || tab === 'events') return tab;
  return 'brain';
}

function setTabUrl(tab) {
  const url = new URL(window.location.href);
  if (tab === 'documents' || tab === 'events') url.searchParams.set('tab', tab);
  else url.searchParams.delete('tab');
  window.history.replaceState(null, '', `${url.pathname}${url.search}`);
}

/**
 * @param {{ productId: string, productName: string, canEdit: boolean, memoryV2?: boolean }} opts
 */
export function renderKnowledge(opts) {
  const root = el('div', { class: 'kb' });
  if (!opts.productId) {
    root.append(empty({
      title: 'Pick a product',
      body: 'Knowledge is kept per product. Choose one in the top bar.',
    }));
    return root;
  }
  const state = {
    tab: tabFromUrl(),
    canEdit: opts.canEdit,
    memoryV2: !!opts.memoryV2,
    productName: opts.productName || 'This product',
    loading: true,
    data: null,
    openInstructions: false,
    uploadOpen: false,
    jobs: [],
    pumping: false,
    memoryTab: 'fact',
    memoryPermanent: false,
    memoryQuery: '',
    memorySearch: null,
    instructionQuery: '',
    instructionSearch: null,
    docsQuery: '',
    docsSearch: null,
    navGen: 0,
    docsOpening: false,
  };
  const head = el('div');
  const stats = el('div');
  const body = el('div');
  root.append(head, stats, body);
  paint();
  refresh();
  return root;

  function paint() {
    clear(head);
    clear(stats);
    clear(body);
    head.append(header());
    stats.append(statStrip());
    body.append(pageBody());
  }

  async function refresh() {
    state.loading = true;
    paint();
    await refreshQuiet();
  }

  async function refreshQuiet() {
    const documentsTab = state.tab === 'documents';
    const data = await loadKnowledge(opts.productId, { documentsTab });
    if (!documentsTab && state.tab === 'documents' && data && data.documents === 'ready') {
      const page = await fetchDocumentPage(opts.productId, { limit: DOC_PAGE, offset: 0 });
      if (page && !page.error) {
        data.docs = page.documents || [];
        const total = Number(page.total);
        data.docsTotal = Number.isFinite(total) ? total : data.docs.length;
        data.docsMode = 'page';
      }
    }
    state.loading = false;
    state.data = data;
    state.memoryQuery = '';
    state.memorySearch = null;
    state.memoryTab = 'fact';
    state.instructionQuery = '';
    state.instructionSearch = null;
    state.docsQuery = '';
    state.docsSearch = null;
    paint();
    if (data && data.documents === 'ready') {
      const count = data.docsTotal || (data.docs && data.docs.length) || 0;
      if (claimReindex(opts.productId, opts.canEdit, count)) {
        api('/documents/reindex', { method: 'POST', body: reindexBody(opts.productId) }).then(() => {});
      }
    }
    openLinkedDocument(opts.productId);
  }

  function applyMemory(memory) {
    if (!state.data || !memory || !memory.id) return;
    const group = memoryGroup(memory.memory_type);
    if (group === 'instruction') {
      const standing = { rows: state.data.instructions, total: state.data.instructionTotal };
      upsertCounted(standing, state.data.memoryCounts, 'instruction', memory);
      state.data.instructions = standing.rows;
      state.data.instructionTotal = standing.total;
    } else if (state.data.memoryLists[group]) {
      upsertCounted(state.data.memoryLists[group], state.data.memoryCounts, group, memory);
      if (state.memorySearch) state.memorySearch.rows = upsertRow(state.memorySearch.rows, memory);
    }
    paint();
  }

  function dropMemory(id) {
    if (!state.data) return;
    for (const key of ['fact', 'decision', 'todo']) {
      dropCounted(state.data.memoryLists[key], state.data.memoryCounts, key, id);
    }
    const standing = { rows: state.data.instructions, total: state.data.instructionTotal };
    dropCounted(standing, state.data.memoryCounts, 'instruction', id);
    state.data.instructions = standing.rows;
    state.data.instructionTotal = standing.total;
    if (state.memorySearch) {
      state.memorySearch.rows = state.memorySearch.rows.filter((row) => row.id !== id);
    }
    if (state.instructionSearch) {
      state.instructionSearch.rows = state.instructionSearch.rows.filter((row) => row.id !== id);
    }
    paint();
  }

  function header() {
    const count = state.data ? state.data.docsTotal : 0;
    return el('header', { class: 'kb-head kb-rv' },
      el('div', { class: 'kb-head-row' },
        el('div', null,
          el('h1', { class: 'kb-title' }, opts.productName || 'This product', '’s ', el('em', null, 'brain.')),
          el('p', { class: 'kb-lead' }, 'Everything Bonnie carries into an answer about this product — all of it editable by hand.')),
        el('div', { class: 'kb-actions' },
          el('button', {
            class: 'kb-btn',
            type: 'button',
            onClick() {
              if (!state.canEdit) {
                toast('An admin can change instructions.', 'error');
                return;
              }
              state.openInstructions = true;
              state.tab = 'brain';
              setTabUrl('brain');
              paint();
              document.getElementById('kb-instructions')?.scrollIntoView({ block: 'nearest' });
              document.querySelector('#kb-instructions .kb-instr-input')?.focus();
            },
          }, 'Write Instructions'),
          uploadButton())),
      el('div', { class: 'kb-tabs', role: 'tablist', 'aria-label': 'Knowledge views' },
        tabButton('brain', 'Brain'),
        tabButton('documents', 'Documents ', el('span', { class: 'kb-tab-n' }, String(count))),
        tabButton('events', 'Event names')));
  }

  function tabButton(id, ...label) {
    return el('button', {
      class: state.tab === id ? 'kb-tab is-on' : 'kb-tab',
      type: 'button',
      role: 'tab',
      'aria-selected': state.tab === id ? 'true' : 'false',
      onClick() {
        if (id === 'documents') {
          showDocuments();
          return;
        }
        state.navGen += 1;
        state.tab = id;
        setTabUrl(id);
        paint();
      },
    }, ...label);
  }

  function statStrip() {
    if (state.loading) return el('div', { class: 'kb-stats kb-rv kb-rv-2' }, skeleton(3));
    if (!state.data) return null;
    const counts = state.data.memoryCounts || { fact: 0, decision: 0, todo: 0 };
    const memoryCount = state.data.memory === 'ready'
      ? counts.fact + counts.decision + counts.todo
      : '—';
    return el('div', { class: 'kb-stats kb-rv kb-rv-2' },
      stat('Documents', state.data.documents === 'ready' ? String(state.data.docsTotal) : '—', 'All in this product'),
      stat('Memories', String(memoryCount), state.data.memory === 'ready'
        ? memorySplit(counts)
        : 'Could not be counted'),
      learnedStat(state.data.newest));
  }

  function pageBody() {
    if (state.tab === 'events') {
      return el('div', { class: 'kb-body' },
        el('div', { class: 'kb-main' }, renderEvents({
          productId: opts.productId,
          canEdit: state.canEdit,
        })),
        sideSlot());
    }
    return el('div', { class: 'kb-body' },
      el('div', { class: 'kb-main' },
        state.uploadOpen ? renderDropZone({
          jobs: state.jobs,
          onFiles: queueFiles,
          onRetry: retryJob,
        }) : null,
        instructionsSlot(),
        documentsSlot(),
        state.tab === 'brain' ? memorySlot() : null),
      sideSlot());
  }

  function uploadButton() {
    return el('button', {
      class: 'kb-btn kb-btn-fill',
      type: 'button',
      disabled: state.canEdit ? null : true,
      onClick() {
        state.uploadOpen = true;
        paint();
        document.getElementById('kb-drop')?.scrollIntoView({ block: 'nearest' });
      },
    }, 'Upload ', el('span', null, '→'));
  }

  function queueFiles(list) {
    const files = [...(list || [])];
    const skipped = files.filter((file) => !kbFileAllowed(file));
    if (skipped.length) toast('Audio and video are skipped. Upload documents or images.', 'error');
    files.filter(kbFileAllowed).forEach((file, index) => {
      const tooBig = file.size > KB_MAX_BYTES;
      state.jobs.push({
        key: `up-${Date.now()}-${index}-${file.name}`,
        name: file.name,
        file,
        phase: tooBig ? 'error' : 'queued',
        pct: 0,
        error: tooBig ? 'File too large (max 40 MB)' : '',
        doc: null,
      });
    });
    state.uploadOpen = true;
    paintJobs();
    pump();
  }

  function paintJobs() {
    const current = root.querySelector('.kb-uploads');
    if (!current) {
      paint();
      return;
    }
    current.replaceWith(renderDropZone({
      jobs: state.jobs,
      onFiles: queueFiles,
      onRetry: retryJob,
    }));
  }

  function retryJob(key) {
    const job = state.jobs.find((item) => item.key === key);
    if (!job) return;
    job.phase = 'queued';
    job.error = '';
    job.pct = 0;
    paintJobs();
    pump();
  }

  async function pump() {
    if (state.pumping) return;
    state.pumping = true;
    try {
      let next = state.jobs.find((job) => job.phase === 'queued');
      while (next) {
        await runJob(next);
        next = state.jobs.find((job) => job.phase === 'queued');
      }
    } finally {
      state.pumping = false;
    }
  }

  async function runJob(job) {
    job.phase = 'upload';
    job.pct = 0;
    paintJobs();
    const result = await uploadDocument(job.file, opts.productId, {
      onProgress(pct) {
        job.phase = 'upload';
        job.pct = pct;
        paintJobs();
      },
      onIngest() {
        job.phase = 'ingest';
        paintJobs();
      },
    });
    if (!result || result.error || !(result.document || result.id)) {
      job.phase = 'error';
      job.error = (result && result.error) || 'Upload failed';
      paintJobs();
      return;
    }
    job.phase = 'done';
    job.doc = result.document || result;
    addDocument(job.doc);
  }

  function addDocument(doc) {
    if (!state.data || !doc || !doc.id) return;
    const known = state.data.docs.some((row) => row.id === doc.id);
    const rest = state.data.docs.filter((row) => row.id !== doc.id);
    state.data.docs = [doc, ...rest];
    if (!known) state.data.docsTotal += 1;
    state.data.documents = 'ready';
    paint();
  }

  function documentsSlot() {
    if (state.loading || !state.data) return skeleton(2);
    return renderDocuments({
      docs: state.data.docs,
      total: state.data.docsTotal,
      status: state.data.documents,
      error: state.data.documentsError,
      tab: state.tab,
      query: state.docsQuery,
      search: state.docsSearch,
      productId: opts.productId,
      onRefresh: refresh,
      onViewAll: showDocuments,
      onPage: pageDocuments,
      onRemember: rememberDocs,
    });
  }

  async function showDocuments() {
    state.tab = 'documents';
    setTabUrl('documents');
    const generation = ++state.navGen;
    if (state.data && state.data.docsMode === 'recent' && state.data.documents === 'ready' && !state.docsOpening) {
      state.docsOpening = true;
      try {
        const result = await fetchDocumentPage(opts.productId, { limit: DOC_PAGE, offset: 0 });
        if (generation !== state.navGen) return;
        if (!result || result.error) {
          toast((result && result.error) || 'Could not load documents', 'error');
        } else {
          state.data.docs = result.documents || [];
          const total = Number(result.total);
          state.data.docsTotal = Number.isFinite(total) ? total : state.data.docs.length;
          state.data.docsMode = 'page';
        }
      } finally {
        state.docsOpening = false;
      }
    }
    if (generation !== state.navGen) return;
    paint();
  }

  async function pageDocuments(spec) {
    const q = String(spec.q || '').trim();
    const result = await fetchDocumentPage(opts.productId, { q, offset: spec.offset || 0, limit: DOC_PAGE });
    if (!result || result.error) {
      return { ok: false, error: (result && result.error) || 'Could not load documents', rows: [], total: 0 };
    }
    const incoming = result.documents || [];
    const total = Number(result.total);
    const count = Number.isFinite(total) ? total : incoming.length;
    if (!q && state.data) {
      state.data.docs = spec.offset > 0 ? mergeRows(state.data.docs, incoming) : incoming;
      state.data.docsTotal = count;
      state.data.docsMode = 'page';
    }
    return { ok: true, rows: incoming, total: count };
  }

  function rememberDocs(q, rows, total) {
    state.docsQuery = String(q || '').trim();
    state.docsSearch = state.docsQuery && rows ? { rows, total } : null;
  }

  function sideSlot() {
    if (state.loading || !state.data) {
      return el('aside', { class: 'kb-aside' }, skeleton(3), skeleton(3));
    }
    return renderSide({
      breakdown: state.data.breakdown,
      depthStatus: state.data.depth,
      depthError: state.data.depthError,
      sources: state.data.sources,
      connectorsStatus: state.data.connectors,
      connectorsError: state.data.connectorsError,
      productId: opts.productId,
      canEdit: opts.canEdit,
      onRefresh: refresh,
    });
  }

  function memoryFetchOpts(extra) {
    return {
      permanence: state.memoryPermanent ? 'permanent' : '',
      ...extra,
    };
  }

  function memorySlot() {
    if (state.loading || !state.data) return skeleton(2);
    return renderMemory({
      lists: state.data.memoryLists,
      counts: state.data.memoryCounts,
      tab: state.memoryTab,
      query: state.memoryQuery,
      search: state.memorySearch,
      status: state.data.memory,
      error: state.data.memoryError,
      canEdit: state.canEdit,
      memoryV2: state.memoryV2,
      permanentOnly: state.memoryPermanent,
      onTab(type) {
        state.memoryTab = type;
        if (state.memoryPermanent && state.data.memoryLists[type]) {
          state.data.memoryLists[type].loaded = false;
        }
      },
      onPermanent(on) {
        state.memoryPermanent = !!on;
        Object.values(state.data.memoryLists).forEach((list) => { list.loaded = false; });
      },
      onEnsure: ensureMemory,
      onMore: moreMemory,
      onSearch: searchMemory,
      onRemember(q, search) {
        state.memoryQuery = q;
        state.memorySearch = search;
      },
      onClear() {
        state.memoryQuery = '';
        state.memorySearch = null;
      },
      onSaved: applyMemory,
      onDeleted: dropMemory,
      onRefresh: () => (state.data && state.data.memory === 'ready' ? confirmMemory() : refresh()),
    });
  }

  function adoptSummary(result) {
    if (!state.data || !result) return;
    if (result.counts) state.data.memoryCounts = countsOf(result);
    if (result.newest_at) state.data.newest = result.newest_at;
  }

  async function ensureMemory(type) {
    const list = state.data.memoryLists[type];
    if (list.loaded) return { ok: true };
    const result = await fetchMemoryPage(opts.productId, memoryFetchOpts({
      type, offset: 0, limit: MEMORY_PAGE,
    }));
    if (!result || result.error) return { ok: false, error: (result && result.error) || 'Could not load memories' };
    list.rows = result.memories || [];
    const total = Number(result.total);
    list.total = Number.isFinite(total) ? total : list.rows.length;
    list.loaded = true;
    adoptSummary(result);
    return { ok: true };
  }

  async function moreMemory(type) {
    const list = state.data.memoryLists[type];
    const result = await fetchMemoryPage(opts.productId, memoryFetchOpts({
      type, offset: list.rows.length, limit: MEMORY_PAGE,
    }));
    if (!result || result.error) return { ok: false, error: (result && result.error) || 'Could not load memories' };
    list.rows = mergeRows(list.rows, result.memories || []);
    const total = Number(result.total);
    if (Number.isFinite(total)) list.total = total;
    adoptSummary(result);
    return { ok: true };
  }

  async function searchMemory(q, offset) {
    const result = await fetchMemoryPage(opts.productId, memoryFetchOpts({
      type: '', q, offset: offset || 0, limit: MEMORY_PAGE,
    }));
    if (!result || result.error) {
      return { ok: false, error: (result && result.error) || 'Could not search memories', rows: [], total: 0 };
    }
    const incoming = result.memories || [];
    const total = Number(result.total);
    const count = Number.isFinite(total) ? total : incoming.length;
    adoptSummary(result);
    return { ok: true, rows: incoming, total: count };
  }

  async function confirmMemory() {
    const type = state.memoryTab;
    const query = state.memoryQuery.trim();
    const loaded = query && state.memorySearch ? state.memorySearch.rows.length : state.data.memoryLists[type].rows.length;
    const limit = Math.min(PAGE_CAP, Math.max(MEMORY_PAGE, loaded || MEMORY_PAGE));
    const result = await fetchMemoryPage(opts.productId, memoryFetchOpts({
      type: query ? '' : type, q: query, offset: 0, limit,
    }));
    if (result && !result.error) {
      const rows = result.memories || [];
      const total = Number(result.total);
      const count = Number.isFinite(total) ? total : rows.length;
      if (query) state.memorySearch = { q: query, rows, total: count };
      else {
        const list = state.data.memoryLists[type];
        list.rows = rows;
        list.total = count;
        list.loaded = true;
      }
      adoptSummary(result);
    }
    paint();
  }

  function instructionsSlot() {
    if (state.loading || !state.data) return skeleton(2);
    const open = state.openInstructions;
    state.openInstructions = false;
    return renderInstructions({
      rows: state.instructionSearch ? state.instructionSearch.rows : state.data.instructions,
      total: state.instructionSearch ? state.instructionSearch.total : state.data.instructionTotal,
      browseRows: state.data.instructions,
      browseTotal: state.data.instructionTotal,
      query: state.instructionQuery,
      search: state.instructionSearch,
      status: state.data.instructionStatus,
      error: state.data.instructionError,
      canEdit: state.canEdit,
      productId: opts.productId,
      openEditor: open,
      onPage: pageInstructions,
      onRemember(q, search) {
        state.instructionQuery = String(q || '').trim();
        state.instructionSearch = search;
      },
      onSaved: applyMemory,
      onDeleted: dropMemory,
      onRefresh: () => (state.data && state.data.instructionStatus === 'ready' ? confirmInstructions() : refresh()),
    });
  }

  async function pageInstructions(spec) {
    const q = String(spec.q || '').trim();
    const result = await fetchMemoryPage(opts.productId, {
      type: 'instruction', q, offset: spec.offset || 0, limit: MEMORY_PAGE,
    });
    if (!result || result.error) {
      return { ok: false, error: (result && result.error) || 'Could not load instructions', rows: [], total: 0 };
    }
    const incoming = result.memories || [];
    const total = Number(result.total);
    const count = Number.isFinite(total) ? total : incoming.length;
    if (!q && state.data) {
      state.data.instructions = spec.offset > 0 ? mergeRows(state.data.instructions, incoming) : incoming;
      state.data.instructionTotal = count;
    }
    adoptSummary(result);
    return { ok: true, rows: incoming, total: count };
  }

  async function confirmInstructions() {
    const query = state.instructionQuery.trim();
    const loaded = query && state.instructionSearch
      ? state.instructionSearch.rows.length
      : state.data.instructions.length;
    const limit = Math.min(PAGE_CAP, Math.max(MEMORY_PAGE, loaded || MEMORY_PAGE));
    const result = await fetchMemoryPage(opts.productId, {
      type: 'instruction', q: query, offset: 0, limit,
    });
    if (result && !result.error) {
      const rows = result.memories || [];
      const total = Number(result.total);
      const count = Number.isFinite(total) ? total : rows.length;
      if (query) state.instructionSearch = { q: query, rows, total: count };
      else {
        state.data.instructions = rows;
        state.data.instructionTotal = count;
        state.instructionSearch = null;
      }
      adoptSummary(result);
    }
    paint();
  }
}

function stat(label, figure, sub) {
  return el('div', { class: 'kb-stat' },
    el('span', { class: 'kb-kicker' }, label),
    el('span', { class: 'kb-figure' }, figure),
    el('span', { class: 'kb-sub' }, sub));
}

function learnedStat(iso) {
  const relative = iso ? formatRelative(iso) : '';
  const figure = relative && relative !== '—'
    ? el('span', { class: 'kb-figure' }, el('em', null, relative), ' ago')
    : el('span', { class: 'kb-figure' }, 'never');
  return el('div', { class: 'kb-stat' },
    el('span', { class: 'kb-kicker' }, 'Last learned'),
    figure,
    el('span', { class: 'kb-sub' }, 'Newest memory added'));
}

